import ViewModel from "../ViewModel";
import TableViewModel from "./TableViewModel";
import DashboardRepository from "../../../domain/repositories/dashboard/DashboardRepository";
import CountryHolder from "../../../domain/models/country/CountryHolder";
import ProductItem from "../../../domain/models/dashboard/ProductItem";
import SearchResponse from "../../../domain/models/dashboard/SearchResponse";
import CountryItem from "../../../domain/models/country/CountryItem";
import BestSellerItem from "../../../domain/models/dashboard/BestSellerItem";
import BaseView from "../../view/BaseView";

export default class TableViewModelImpl extends ViewModel implements TableViewModel {
    public productSearchQuery: string;
    public isProductsSearching: boolean;

    public isShowSearchFinished: boolean;
    public items: ProductItem[];
    public displayingItems: ProductItem[];

    public lastSearchCountry?: CountryItem;

    public bestSellers: BestSellerItem[];

    private dashboardRepository: DashboardRepository;
    private countryHolder: CountryHolder;

    private searchInterval?: NodeJS.Timeout;
    private lastSearchTime?: number;

    private sortedColumnName?: string;
    private isSortUp: boolean;

    public constructor(dashboardRepository: DashboardRepository, countryHolder: CountryHolder) {
        super();

        this.dashboardRepository = dashboardRepository;
        this.countryHolder = countryHolder;

        this.productSearchQuery = "";
        this.isProductsSearching = false;
        this.items = [];
        this.isShowSearchFinished = false;
        this.displayingItems = [];
        this.isSortUp = true;
        this.bestSellers = [];
    }

    public attachView = async (baseView: BaseView): Promise<void> => {
        console.log("atach");
        super.attachView(baseView);

        this.bestSellers = await this.dashboardRepository.getBestSellers();
        super.notifyViewAboutChanges();
    };

    public onSortColumn = (columnName: string | undefined, isSortUp: boolean, offset: number, limit: number): void => {
        this.sortedColumnName = columnName;
        this.isSortUp = isSortUp;

        const itemsCopy: ProductItem[] = JSON.parse(JSON.stringify(this.items));
        this.displayingItems = itemsCopy
            .sort((firstProduct, secondProduct) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                let firstColumnValue = firstProduct[this.sortedColumnName];
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                let secondColumnValue = secondProduct[this.sortedColumnName];

                if (!Number.isNaN(firstColumnValue)) firstColumnValue = Number(firstColumnValue);
                if (!Number.isNaN(secondColumnValue)) secondColumnValue = Number(secondColumnValue);

                if (this.sortedColumnName) {
                    if (firstColumnValue > secondColumnValue) {
                        if (this.isSortUp) {
                            return -1;
                        }
                        return 1;
                    } else {
                        if (this.isSortUp) {
                            return 1;
                        }
                        return -1;
                    }
                }

                return 0;
            })
            .splice(offset, offset + limit);

        super.notifyViewAboutChanges();
    };

    public fetchItems = (offset: number = 0, limit: number = 0): void => {
        const itemsCopy: ProductItem[] = JSON.parse(JSON.stringify(this.items));
        this.displayingItems = itemsCopy
            .sort((firstProduct, secondProduct) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                let firstColumnValue = firstProduct[this.sortedColumnName];
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                let secondColumnValue = secondProduct[this.sortedColumnName];

                if (!Number.isNaN(firstColumnValue)) firstColumnValue = Number(firstColumnValue);
                if (!Number.isNaN(secondColumnValue)) secondColumnValue = Number(secondColumnValue);

                if (this.sortedColumnName) {
                    if (firstColumnValue > secondColumnValue) {
                        if (this.isSortUp) {
                            return -1;
                        }
                        return 1;
                    } else {
                        if (this.isSortUp) {
                            return 1;
                        }
                        return -1;
                    }
                }

                return 0;
            })
            .splice(offset, offset + limit);
        super.notifyViewAboutChanges();
    };

    public onSearchRandomProducts = async (): Promise<void> => {
        this.onProductSearchQueryChanged("");

        this.items = [];
        this.displayingItems = [];
        this.isProductsSearching = true;
        this.lastSearchCountry = this.countryHolder.getCountry();
        super.notifyViewAboutChanges();

        try {
            const response = await this.dashboardRepository.searchRandomProducts(
                this.countryHolder.getCountry().countryAbbreviation
            );

            if ((response as SearchResponse).search_query_id) {
                if ((response as SearchResponse).niche_url) {
                    const win = window.open((response as SearchResponse).niche_url, "_blank");
                    if (win) {
                        win.blur();
                    }
                    window.focus();
                }

                this.startSearch((response as SearchResponse).search_query_id as string);
            } else {
                this.items = response as ProductItem[];
                this.isProductsSearching = false;
                super.notifyViewAboutChanges();
            }
        } catch (e) {
            if (e instanceof Error) {
                alert(e?.message);
                this.isProductsSearching = false;
            } else {
                alert(String(e));
                this.isProductsSearching = false;
            }
        }
    };

    public onSearchProduct = async (): Promise<void> => {
        if (this.productSearchQuery) {
            this.items = [];
            this.displayingItems = [];
            this.isProductsSearching = true;
            this.lastSearchCountry = this.countryHolder.getCountry();
            super.notifyViewAboutChanges();

            try {
                const response = await this.dashboardRepository.searchProducts(
                    this.countryHolder.getCountry().countryAbbreviation,
                    this.productSearchQuery
                );

                if ((response as SearchResponse).search_query_id) {
                    this.startSearch((response as SearchResponse).search_query_id as string);
                } else {
                    this.items = response as ProductItem[];
                    this.isProductsSearching = false;
                    super.notifyViewAboutChanges();
                }
            } catch (e) {
                if (e instanceof Error) {
                    alert(e?.message);
                    this.isProductsSearching = false;
                } else {
                    alert(String(e));
                    this.isProductsSearching = false;
                }
            }
        }
    };

    public onGenerateSearch = async (): Promise<void> => {
        if (this.productSearchQuery) {
            this.items = [];
            this.displayingItems = [];
            this.isProductsSearching = true;
            this.lastSearchCountry = this.countryHolder.getCountry();
            super.notifyViewAboutChanges();

            try {
                const response = await this.dashboardRepository.searchGenerated(
                    this.countryHolder.getCountry().countryAbbreviation,
                    this.productSearchQuery
                );

                if ((response as SearchResponse).search_query_id) {
                    this.startSearch((response as SearchResponse).search_query_id as string);
                } else {
                    this.items = response as ProductItem[];
                    this.isProductsSearching = false;
                    super.notifyViewAboutChanges();
                }
            } catch (e) {
                if (e instanceof Error) {
                    alert(e?.message);
                    this.isProductsSearching = false;
                } else {
                    alert(String(e));
                    this.isProductsSearching = false;
                }
            }
        }
    };

    public onProductSearchQueryChanged = (query: string): void => {
        this.productSearchQuery = query;
        super.notifyViewAboutChanges();
    };

    public onCloseSearchFinished = (): void => {
        this.isShowSearchFinished = false;
        super.notifyViewAboutChanges();
    };

    private startSearch = async (searchQueryId: string): Promise<void> => {
        if (this.searchInterval) {
            clearInterval(this.searchInterval);
        }

        // const currentSearchTime = Date.now();
        // this.lastSearchTime = currentSearchTime;

        let searchCount = 50;

        this.searchInterval = setInterval(async () => {
            // if (currentSearchTime !== this.lastSearchTime) {
            //   return;
            // }


            this.dashboardRepository
                .pingProducts(searchQueryId)
                .then((products) => {
                    console.log(searchCount, products)
                    if (Array.isArray(products)) {
                        this.addNewProducts(products);
                    } else {
                        if (this.searchInterval) {
                            clearInterval(this.searchInterval);
                        }
                        this.isProductsSearching = false;
                        this.isShowSearchFinished = true;
                        super.notifyViewAboutChanges();
                        return;
                    }
                    if (searchCount < 0) {
                        if (this.searchInterval) {
                            clearInterval(this.searchInterval);
                        }
                        this.isProductsSearching = false;
                        this.isShowSearchFinished = true;
                        super.notifyViewAboutChanges();
                        return;
                    }
                    searchCount = searchCount - 1;
                })
                .catch((error) => {
                    if (error.message === "204") {
                        if (searchCount < 0) {
                            if (this.searchInterval) {
                                clearInterval(this.searchInterval);
                            }
                            this.isProductsSearching = false;
                            this.isShowSearchFinished = true;
                            super.notifyViewAboutChanges();
                            return;
                        }
                        searchCount = searchCount - 1;
                    } else {
                        alert(error.message);
                        throw error;
                    }
                });
        }, 2000);
    };

    private addNewProducts = (products: ProductItem[]): void => {
        const oldItemsIds = this.items.map((product) => product.id);
        products
            .filter((product) => !oldItemsIds.includes(product.id))
            .forEach((newProduct) => {
                if (this.items.length < 25) {
                    this.items.push(newProduct);
                }
            });

        if (this.items.length >= 25) {
            this.isProductsSearching = false;
            this.lastSearchTime = undefined;
            if (this.searchInterval) {
                clearInterval(this.searchInterval);
            }
            this.isShowSearchFinished = true;
        }

        this.items = this.items.sort((firstProduct, secondProduct) => {
            let firstColumnValue = firstProduct.estimated_sales;
            let secondColumnValue = secondProduct.estimated_sales;

            if (!Number.isNaN(firstColumnValue)) firstColumnValue = Number(firstColumnValue);
            if (!Number.isNaN(secondColumnValue)) secondColumnValue = Number(secondColumnValue);

            if (firstColumnValue > secondColumnValue) {
                return -1;
            } else {
                if (firstColumnValue === secondColumnValue) {
                    return 0;
                }

                return 1;
            }
        });

        super.notifyViewAboutChanges();
    };
}
